import {Component, inject, Input, OnChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, of, Subject, switchMap} from 'rxjs';
import {
    FacilityUtilizationScoreQuery,
    FacilityUtilizationScoreType,
    GetYearToDateSummaryQuery,
    IMyScoreShiftQuery,
    InventoryClient,
    ProductivityClient,
    YearToDateScoreQuery,
} from '@services';
import {DoughnutChartComponent} from '../../../components/library/doughnut-chart/doughnut-chart.component';
import {CapitalCasePipe} from '../../../pipes/capital-case.pipe';
import {CardItem} from '../../attendance/my-score/summary-card/summary-card.component';
import {yearToDatePalette, yearToDateSummaryCards} from '../../productivity/year-to-date/year-to-date-score/year-to-date-score.component';
import {facilityUtilizationPalette} from '../../inventory/inventory-utilization/facility-utilization-score/facility-utilization-score.component';
import {DashboardAccess} from '../dashboard-access';
import {DashboardCardComponent} from '../dashboard-card/dashboard-card.component';
import {DashboardTilesComponent} from '../dashboard-tiles/dashboard-tiles.component';

@Component({
    selector: 'app-dashboard-volumes',
    imports: [DoughnutChartComponent, CapitalCasePipe, DashboardCardComponent, DashboardTilesComponent],
    templateUrl: './dashboard-volumes.component.html',
})
export class DashboardVolumesComponent implements OnChanges {
    @Input() from: Date;
    @Input() to: Date;

    protected access = inject(DashboardAccess);
    private productivityClient = inject(ProductivityClient);
    private inventoryClient = inject(InventoryClient);

    protected readonly palettes = {
        throughput: yearToDatePalette(),
        facilityUtilization: facilityUtilizationPalette(),
    };

    protected readonly usageCharts = [
        FacilityUtilizationScoreType.FacilityUsage,
        FacilityUtilizationScoreType.CoolerUsage,
        FacilityUtilizationScoreType.AmbientUsage,
    ].map(scoreType => ({
        scoreType,
        loadData: (query: IMyScoreShiftQuery) =>
            this.inventoryClient.getFacilityUtilizationScore(new FacilityUtilizationScoreQuery({...query, scoreType})),
    }));

    protected throughputTiles: CardItem[] = [];

    private readonly dateRange$ = new Subject<[Date, Date]>();

    constructor() {
        this.dateRange$.pipe(
            switchMap(([from, to]) => from && this.access.throughput
                ? this.productivityClient.getYearToDateSummary(new GetYearToDateSummaryQuery({from, to})).pipe(catchError(() => of(null)))
                : of(null)),
            takeUntilDestroyed(),
        ).subscribe(summary => {
            this.throughputTiles = summary
                ? yearToDateSummaryCards(summary)
                : this.throughputTiles.map(tile => ({...tile, value: 0}));
        });
    }

    protected loadThroughput = (query: IMyScoreShiftQuery) =>
        this.productivityClient.getYearToDateScore(new YearToDateScoreQuery(query));

    ngOnChanges(): void {
        this.dateRange$.next([this.from, this.to]);
    }
}
